// Settings → Site settings → Install: the picked site's script tags, standard and first-party (sdk/proxy).

interface InstallInfo {
  project: string;
  name: string;
  write_key: string;
  domains: string[];
  site: string | null;
  standard: string;
  first_party: string;
  proxy_prefix: string;
  docs: string;
}

const esc = (s: string) => s.replace(/[&<>"']/g, (c) => `&#${c.charCodeAt(0)};`);

const block = (id: string, code: string) =>
  `<div class="inst-code"><pre id="${id}"><code>${esc(code)}</code></pre><button class="btn" type="button" data-copy="${id}">Copy</button></div>`;

/** Fills `box` with the Install card for project `id` (a single site; org views have no snippet). */
export async function renderInstall(box: HTMLElement, project: { id: string; name: string; kind?: string }, current: () => boolean = () => true): Promise<void> {
  if (project.kind === "org") {
    box.innerHTML = `<div class="card"><div class="card-head"><h2>Install</h2></div><div class="acct-msg">Pick a single site in the project menu to see its snippet.</div></div>`;
    return;
  }
  box.innerHTML = `<div class="card"><div class="card-head"><h2>Install</h2></div><div class="acct-msg">Loading…</div></div>`;
  let info: InstallInfo;
  try {
    const res = await fetch(`/api/install?project=${encodeURIComponent(project.id)}`, { credentials: "same-origin" });
    const out = await res.json().catch(() => ({ error: `HTTP ${res.status}` }));
    if (!res.ok) throw new Error((out as { error?: string }).error ?? `HTTP ${res.status}`);
    info = out as InstallInfo;
  } catch (err) {
    if (!current()) return;
    box.innerHTML = `<div class="card"><div class="card-head"><h2>Install</h2></div><div class="acct-msg error">Couldn't load the snippet (${esc((err as Error).message)}).</div></div>`;
    return;
  }
  if (!current()) return;
  const site = info.site ?? "example.com";
  // "null" lets a Shopify pixel's sandboxed pages through (migration 0024); it isn't a domain.
  const shown = info.domains.filter((d) => d.trim() && d.trim().toLowerCase() !== "null");
  const p = info.proxy_prefix;
  box.innerHTML = `<div class="card inst-card">
  <div class="card-head"><h2>Install</h2></div>
  <section>
    <h3>Standard</h3>
    <p class="muted">In the <code>&lt;head&gt;</code> of every page. Loads from t.monoblock.ae.</p>
    ${block("inst-std", info.standard)}
  </section>
  <section>
    <h3>First-party <span class="pill good">Recommended</span></h3>
    <p class="muted">The script and its events go through the site's own domain (<code>${esc(site)}${esc(p)}/s.js</code> and <code>${esc(site)}${esc(p)}/e</code>), so ad blockers that list t.monoblock.ae don't drop visits.
    The site needs a small proxy at <code>${esc(p)}/*</code> holding a proxy key: a Cloudflare Worker, or a server route in the app.
    <a href="${esc(info.docs)}" target="_blank" rel="noopener">Proxy setup</a></p>
    ${block("inst-fp", info.first_party)}
    <p class="muted">Until the proxy is live, <code>${esc(p)}/s.js</code> is a 404 and nothing is counted. To fall back to the standard script meanwhile, add <code>onerror</code>:</p>
    ${block("inst-fb", info.first_party.replace("></script>", ` onerror="var s=document.createElement('script');s.defer=1;s.src='https://t.monoblock.ae/s.js';s.dataset.key=this.dataset.key;document.head.appendChild(s)"></script>`))}
  </section>
  <p class="muted inst-foot">Write key <code>${esc(info.write_key)}</code> (public). Allowed domains: ${shown.length ? shown.map((d) => `<code>${esc(d)}</code>`).join(", ") : "none yet, so every event is refused"}.</p>
</div>`;
  box.querySelectorAll<HTMLButtonElement>("[data-copy]").forEach((b) =>
    b.addEventListener("click", () => {
      const code = box.querySelector(`#${b.dataset.copy}`)?.textContent ?? "";
      void navigator.clipboard?.writeText(code).then(() => {
        b.textContent = "Copied";
        setTimeout(() => (b.textContent = "Copy"), 1500);
      });
    }),
  );
}
